import React from 'react';

// Illustrative, fictional sample of the family alignment readout. All text is
// real DOM (not SVG) so it renders crisply in the brand typeface. Tight,
// instrument-like layout: a fixed label column, slim range bars and crisp
// ringed markers on a precise grid.

const MEMBERS = [
  { key: 'g1', label: 'Founder', color: '#1C1917' },
  { key: 'daughter', label: 'Daughter', color: '#3D5347' },
  { key: 'son', label: 'Son', color: '#5E7D6F' },
  { key: 'spouse', label: 'Son’s spouse', color: '#A8A29E' },
  { key: 'g3', label: 'Grandchild', color: '#A3BAA9' },
];

const DIMENSIONS = [
  { name: 'Shared Values', scores: [4.6, 4.4, 4.8, 4.5, 4.3] },
  { name: 'Legacy Vision', scores: [4.3, 3.6, 4.4, 3.3, 3.9] },
  { name: 'Wealth Stewardship', scores: [4.8, 2.6, 3.4, 2.2, 3.0] },
  { name: 'Family Governance', scores: [4.5, 2.8, 2.4, 3.6, 2.0] },
  { name: 'Communication', scores: [3.8, 3.0, 4.0, 2.6, 3.4] },
  { name: 'Next-Gen Readiness', scores: [4.7, 2.4, 3.0, 2.8, 2.2] },
];

const GRID =
  'flex flex-col gap-2.5 sm:grid sm:grid-cols-[210px_1fr] sm:gap-8 sm:items-center';

const pct = (v) => ((v - 1) / 4) * 100;

const verdict = (spread) => {
  if (spread <= 0.9) return 'Aligned';
  if (spread <= 1.8) return 'Some divergence';
  return 'Divergent';
};

const AlignmentReadout = ({ className = '' }) => {
  return (
    <div
      className={`bg-white border border-secondary-200 rounded-xl overflow-hidden shadow-[0_1px_3px_rgba(28,25,23,0.04)] ${className}`}
    >
      {/* Header */}
      <div className="px-6 sm:px-9 py-6 border-b border-secondary-200/80 flex items-start justify-between gap-6">
        <div>
          <p className="text-[10px] font-semibold text-primary-700 uppercase tracking-[0.22em] mb-2.5">
            Family alignment readout
          </p>
          <p className="text-[13px] text-secondary-500 leading-relaxed max-w-sm">
            Each member answers privately. Tight clusters are alignment; wide
            spreads are where the conversation belongs.
          </p>
        </div>
        <span className="flex-shrink-0 text-[10px] uppercase tracking-[0.18em] text-secondary-400 border border-secondary-200 rounded-full px-2.5 py-1">
          Illustrative
        </span>
      </div>

      {/* Rows */}
      <div className="divide-y divide-secondary-200/70">
        {DIMENSIONS.map((dim, i) => {
          const min = Math.min(...dim.scores);
          const max = Math.max(...dim.scores);
          const avg = dim.scores.reduce((a, b) => a + b, 0) / dim.scores.length;
          const v = verdict(max - min);
          const divergent = v === 'Divergent';

          return (
            <div
              key={dim.name}
              className={`${GRID} px-6 sm:px-9 py-5 sm:py-[1.15rem] animate-fade-in`}
              style={{ animationDelay: `${i * 55}ms`, animationFillMode: 'both' }}
            >
              <div className="min-w-0">
                <p className="text-[15px] font-medium text-secondary-900 leading-tight">
                  {dim.name}
                </p>
                <span
                  className={`inline-block mt-2 text-[10px] uppercase tracking-[0.13em] rounded-full px-2 py-0.5 ${
                    divergent
                      ? 'bg-secondary-800 text-cream'
                      : 'bg-secondary-100 text-secondary-500'
                  }`}
                >
                  {v}
                </span>
              </div>

              <div className="relative h-6">
                {/* baseline */}
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-secondary-200" />
                {/* range bar */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-secondary-300/70"
                  style={{
                    left: `${pct(min)}%`,
                    width: `${Math.max(pct(max) - pct(min), 0.6)}%`,
                  }}
                />
                {/* family average */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-3.5 w-px bg-secondary-400"
                  style={{ left: `${pct(avg)}%` }}
                />
                {/* members */}
                {dim.scores.map((s, mi) => (
                  <span
                    key={MEMBERS[mi].key}
                    className="absolute top-1/2 w-2.5 h-2.5 rounded-full ring-[3px] ring-white"
                    style={{
                      left: `${pct(s)}%`,
                      backgroundColor: MEMBERS[mi].color,
                      transform: 'translate(-50%, -50%)',
                      boxShadow: '0 1px 2px rgba(28,25,23,0.18)',
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Scale + legend */}
      <div className="px-6 sm:px-9 py-5 border-t border-secondary-200/80 space-y-4">
        <div className={GRID}>
          <span />
          <div className="flex justify-between text-[10px] uppercase tracking-[0.16em] text-secondary-400">
            <span>Lower alignment</span>
            <span>Higher alignment</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2.5">
          {MEMBERS.map((m) => (
            <span
              key={m.key}
              className="inline-flex items-center text-[11px] text-secondary-500"
            >
              <span
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: m.color }}
              />
              {m.label}
            </span>
          ))}
          <span className="inline-flex items-center text-[11px] text-secondary-500">
            <span className="w-3.5 border-t border-secondary-400 mr-2" />
            Family average
          </span>
        </div>
      </div>
    </div>
  );
};

export default AlignmentReadout;
